/**
 * Two-step verification setup with a choice of method:
 *   - Authenticator app (TOTP: QR + key + code), with "Get the app" links;
 *   - Secondary email (a 6-digit code mailed to the user's personal address);
 *   - Set it up later (only when the caller passes `onLater`).
 *
 * Both real methods end in `onConfirmed(recoveryCodes)`. The card owns its own
 * Verify button, so a screen only decides what happens before and after.
 */
import { useEffect, useRef, useState } from "react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ChevronRight, CircleAlert, Clock, Lock, Mail, RotateCw, Send, ShieldCheck, Smartphone } from "lucide-react";
import { Ic } from "./icons";
import { Btn, Field, Note } from "./primitives";
import { Otp, otpComplete } from "./Otp";
import { MfaCard } from "./MfaCard";
import { AppStoreLinks } from "./AppStoreLinks";
import { confirmEmail, confirmTotp, enrollEmail, enrollTotp, obErrorCode, obErrorStatus, ObApiError } from "./api";
import type { MfaEnrollment } from "./types";

export type MfaSetupMethod = "app" | "email";

export interface MfaSetupProps {
  /** Masked secondary email the code would go to; null/undefined ⇒ ask for one. */
  secondaryEmailMasked?: string | null;
  /** Offer "Set it up later" and call this when it's picked. */
  onLater?: () => void;
  /** Extra line under "Set it up later", e.g. "14 days left before it's required". */
  laterHint?: React.ReactNode;
  /** A prerequisite isn't met yet (no account): the choices show but are disabled. */
  waiting?: React.ReactNode;
  onConfirmed: (recoveryCodes: string[]) => void;
  /** "Step N" eyebrow; null hides it. */
  step?: number | null;
}

function retryAfterOf(err: unknown): number {
  const raw = err instanceof ObApiError ? err.body?.retry_after : undefined;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.ceil(n) : 60;
}

/** Counts down to zero once a second; set(n) restarts it. */
function useCountdown(): [number, (n: number) => void] {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) return;
    const id = window.setTimeout(() => setLeft((v) => Math.max(0, v - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [left]);
  return [left, setLeft];
}

export function MfaSetup({ secondaryEmailMasked, onLater, laterHint, waiting, onConfirmed, step = null }: MfaSetupProps) {
  const [method, setMethod] = useState<MfaSetupMethod | null>(null);
  if (method === "app") return <AppMethod onBack={() => setMethod(null)} onConfirmed={onConfirmed} />;
  if (method === "email")
    return <EmailMethod secondaryEmailMasked={secondaryEmailMasked ?? null} onBack={() => setMethod(null)} onConfirmed={onConfirmed} />;
  return (
    <MethodPicker
      step={step}
      waiting={waiting}
      secondaryEmailMasked={secondaryEmailMasked ?? null}
      onLater={onLater}
      laterHint={laterHint}
      onPick={setMethod}
    />
  );
}

/* ------------------------------------------------------------------ */

function CardHead({ icon, title, step }: { icon: typeof ShieldCheck; title: string; step?: number | null }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span className="ob-tile">
        <Ic icon={icon} />
      </span>
      <div>
        {step != null && <p className="ob-eye">{t("onboarding.kit.step", { defaultValue: "Step {{n}}", n: step })}</p>}
        <h2 className="ob-h3">{title}</h2>
      </div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  padding: "clamp(20px,3cqi,28px)",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

function MethodPicker({
  step,
  waiting,
  secondaryEmailMasked,
  onLater,
  laterHint,
  onPick,
}: {
  step?: number | null;
  waiting?: React.ReactNode;
  secondaryEmailMasked: string | null;
  onLater?: () => void;
  laterHint?: React.ReactNode;
  onPick: (m: MfaSetupMethod) => void;
}) {
  const { t } = useTranslation();
  const off = !!waiting;
  return (
    <section className="ob-glass" style={cardStyle} data-testid="mfa-picker">
      <CardHead icon={ShieldCheck} title={t("onboarding.kit.mfa.title", "Two-step verification")} step={step} />
      {waiting ? (
        <p className="ob-sm" data-testid="mfa-waiting" style={{ margin: 0, display: "flex", gap: 8, alignItems: "center" }}>
          <Ic icon={Lock} size={16} color="#64748B" />
          {waiting}
        </p>
      ) : (
        <p className="ob-sm" style={{ margin: 0 }}>
          {t("onboarding.kit.mfa.chooseBody", "Choose how you'll confirm it's you when you sign in.")}
        </p>
      )}
      <div className="mfa-pick">
        <button type="button" disabled={off} onClick={() => onPick("app")} data-testid="mfa-pick-app">
          <span className="ob-tile">
            <Ic icon={Smartphone} />
          </span>
          <span className="mfa-pick-txt">
            <span className="mfa-pick-t">{t("onboarding.kit.mfa.appTitle", "Authenticator app")}</span>
            <span className="mfa-pick-d">
              {t("onboarding.kit.mfa.appDesc", "Get a fresh code from an app on your phone. Works offline. Most secure.")}
            </span>
          </span>
          <Ic icon={ChevronRight} className="ob-flip" color="#64748B" />
        </button>
        <button type="button" disabled={off} onClick={() => onPick("email")} data-testid="mfa-pick-email">
          <span className="ob-tile">
            <Ic icon={Mail} />
          </span>
          <span className="mfa-pick-txt">
            <span className="mfa-pick-t">{t("onboarding.kit.mfa.emailTitle", "Secondary email")}</span>
            <span className="mfa-pick-d">
              {secondaryEmailMasked ? (
                <>
                  {t("onboarding.kit.mfa.emailDescTo", "We'll email a code to")}{" "}
                  <span dir="ltr">{secondaryEmailMasked}</span>
                </>
              ) : (
                t("onboarding.kit.mfa.emailDesc", "We'll email a code to a personal address you choose.")
              )}
            </span>
          </span>
          <Ic icon={ChevronRight} className="ob-flip" color="#64748B" />
        </button>
        {onLater && (
          <button type="button" data-later="" disabled={off} onClick={onLater} data-testid="mfa-pick-later">
            <span className="ob-tile">
              <Ic icon={Clock} />
            </span>
            <span className="mfa-pick-txt">
              <span className="mfa-pick-t">{t("onboarding.kit.mfa.laterTitle", "Set it up later")}</span>
              <span className="mfa-pick-d">
                {laterHint ?? t("onboarding.kit.mfa.laterDesc", "You can turn it on any time in Settings → Security.")}
              </span>
            </span>
            <Ic icon={ChevronRight} className="ob-flip" color="#64748B" />
          </button>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span className="ob-sm">{t("onboarding.kit.mfa.getApp", "Need an authenticator app? Get one free:")}</span>
        <AppStoreLinks />
      </div>
    </section>
  );
}

function BackLink({ onBack, disabled }: { onBack: () => void; disabled?: boolean }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="ob-link" onClick={onBack} disabled={disabled} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <Ic icon={ArrowLeft} size={16} className="ob-flip" />
      {t("onboarding.kit.mfa.otherMethod", "Choose another method")}
    </button>
  );
}

function ErrLine({ children }: { children: React.ReactNode }) {
  return (
    <span className="ob-err" role="alert">
      <Ic icon={CircleAlert} weight="bold" />
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */

function AppMethod({ onBack, onConfirmed }: { onBack: () => void; onConfirmed: (codes: string[]) => void }) {
  const { t } = useTranslation();
  const [enrolment, setEnrolment] = useState<MfaEnrollment | null>(null);
  const [enrolErr, setEnrolErr] = useState(false);
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  const enrol = async () => {
    setEnrolErr(false);
    try {
      setEnrolment(await enrollTotp());
    } catch {
      setEnrolErr(true);
    }
  };

  useEffect(() => {
    // Enrol once: a second enrol call would replace the secret just scanned.
    if (started.current) return;
    started.current = true;
    void enrol();
  }, []);

  const confirm = async () => {
    if (!otpComplete(code) || busy) return;
    setBusy(true);
    setCodeErr(null);
    try {
      const res = await confirmTotp(code);
      onConfirmed(res.recovery_codes ?? []);
    } catch (err) {
      setCode("");
      setCodeErr(
        obErrorStatus(err) === 400
          ? t("onboarding.invitee.mfa.codeMismatch", "That code didn't match. Codes refresh every 30 seconds, so try the newest one.")
          : t("onboarding.invitee.err.generic", "Check your connection and try again."),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="mfa-app">
      <MfaCard
        qrSvg={enrolment?.qr_svg ?? ""}
        secret={enrolment?.secret ?? ""}
        code={code}
        onCode={(v) => {
          setCode(v);
          setCodeErr(null);
        }}
        error={codeErr}
        step={null}
        disabled={busy}
      />
      {enrolErr && (
        <Note
          kind="bad"
          action={
            <Btn kind="sec" size="s" icon={RotateCw} onClick={() => void enrol()}>
              {t("onboarding.invitee.tryAgain", "Try again")}
            </Btn>
          }
        >
          {t("onboarding.invitee.mfa.enrolFailed", "We couldn't start two-step verification setup.")}
        </Note>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
        <BackLink onBack={onBack} disabled={busy} />
        <span style={{ marginInlineStart: "auto" }} />
        <Btn size="l" disabled={!otpComplete(code)} busy={busy} onClick={() => void confirm()} icon={ShieldCheck}>
          {t("onboarding.kit.mfa.verify", "Verify and turn on")}
        </Btn>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function EmailMethod({
  secondaryEmailMasked,
  onBack,
  onConfirmed,
}: {
  secondaryEmailMasked: string | null;
  onBack: () => void;
  onConfirmed: (codes: string[]) => void;
}) {
  const { t } = useTranslation();
  const [address, setAddress] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useCountdown();

  const needsAddress = !secondaryEmailMasked;
  const addressOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address.trim());

  const send = async () => {
    setSending(true);
    setSendErr(null);
    try {
      const res = await enrollEmail(needsAddress ? { email: address } : {});
      setSentTo(res.sent_to);
      setCode("");
      setCodeErr(null);
      setWait(res.resend_after ?? 60);
    } catch (err) {
      const status = obErrorStatus(err);
      const code = obErrorCode(err);
      if (status === 429) {
        setWait(retryAfterOf(err));
        if (!sentTo) {
          const to = err instanceof ObApiError ? err.body?.sent_to : undefined;
          if (typeof to === "string") setSentTo(to);
        }
        setSendErr(t("onboarding.kit.mfa.emailThrottled", "We just sent a code. Wait a moment before asking for another."));
      } else if (code === "secondary_email_is_login") {
        setSendErr(t("onboarding.kit.mfa.emailIsLogin", "Use a different address from the one you sign in with."));
      } else if (status === 503) {
        setSendErr(t("onboarding.kit.mfa.emailSendFailed", "We couldn't send the code. Try again shortly."));
      } else if (status === 400) {
        setSendErr(t("onboarding.kit.mfa.emailInvalid", "Enter a valid email address."));
      } else {
        setSendErr(t("onboarding.invitee.err.generic", "Check your connection and try again."));
      }
    } finally {
      setSending(false);
    }
  };

  const confirm = async () => {
    if (!otpComplete(code) || busy) return;
    setBusy(true);
    setCodeErr(null);
    try {
      const res = await confirmEmail(code);
      onConfirmed(res.recovery_codes ?? []);
    } catch (err) {
      setCode("");
      setCodeErr(
        obErrorStatus(err) === 400
          ? t("onboarding.kit.mfa.emailCodeMismatch", "That code didn't match or has expired. Check the newest email, or send a new code.")
          : t("onboarding.invitee.err.generic", "Check your connection and try again."),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="mfa-email">
      <section className="ob-glass" style={cardStyle}>
        <CardHead icon={Mail} title={t("onboarding.kit.mfa.emailHeading", "Codes by email")} />
        {!sentTo ? (
          <>
            {needsAddress ? (
              <Field
                label={t("onboarding.kit.mfa.emailLabel", "Your personal email")}
                hint={t("onboarding.kit.mfa.emailHint", "Not your work address. Use an inbox you can open even if you can't sign in here.")}
              >
                <input
                  className="ob-in"
                  type="email"
                  dir="ltr"
                  autoComplete="email"
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    setSendErr(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && addressOk && !sending) void send();
                  }}
                />
              </Field>
            ) : (
              <p className="ob-p" style={{ margin: 0 }}>
                {t("onboarding.kit.mfa.emailWillSend", "We'll send a 6-digit code to")}{" "}
                <strong dir="ltr">{secondaryEmailMasked}</strong>
              </p>
            )}
            {sendErr && <ErrLine>{sendErr}</ErrLine>}
            <div>
              <Btn size="m" icon={Send} busy={sending} disabled={(needsAddress && !addressOk) || wait > 0} onClick={() => void send()}>
                {t("onboarding.kit.mfa.sendCode", "Send code")}
              </Btn>
            </div>
          </>
        ) : (
          <>
            <p className="ob-p" style={{ margin: 0 }} data-testid="mfa-email-sent">
              {t("onboarding.kit.mfa.emailSent", "We sent a 6-digit code to")} <strong dir="ltr">{sentTo}</strong>
              {". "}
              {t("onboarding.kit.mfa.emailExpiry", "It expires in 10 minutes.")}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Otp
                value={code}
                onChange={(v) => {
                  setCode(v);
                  setCodeErr(null);
                }}
                invalid={!!codeErr}
                disabled={busy}
                autoFocus
              />
              {codeErr && <ErrLine>{codeErr}</ErrLine>}
              {sendErr && !codeErr && <ErrLine>{sendErr}</ErrLine>}
            </div>
            <div className="ob-sm" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {t("onboarding.kit.mfa.noEmail", "Didn't get it? Check spam, or")}
              <button type="button" className="ob-link" disabled={wait > 0 || sending} onClick={() => void send()} data-testid="mfa-email-resend">
                {wait > 0
                  ? t("onboarding.kit.mfa.resendIn", { defaultValue: "resend in {{count}}s", count: wait })
                  : t("onboarding.kit.mfa.resend", "resend the code")}
              </button>
            </div>
          </>
        )}
      </section>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
        <BackLink onBack={onBack} disabled={busy} />
        <span style={{ marginInlineStart: "auto" }} />
        {sentTo && (
          <Btn size="l" disabled={!otpComplete(code)} busy={busy} onClick={() => void confirm()} icon={ShieldCheck}>
            {t("onboarding.kit.mfa.verify", "Verify and turn on")}
          </Btn>
        )}
      </div>
    </div>
  );
}
